// Shared per-candidate screening breakdown modal (lab analysis + project Contacted).
// Renders each answer against its qualifying rule (pass / fail / neutral), a verdict
// banner, and a resume download. Callers pass normalized props rather than a raw row so
// any response shape can feed it.
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { X, CheckCircle2, XCircle, Info, Download } from "lucide-react";
import { CandidateAvatar } from "../CandidateComponents";
import { LinkedInLink } from "./responseBadges";

const SOURCING_URL = process.env.REACT_APP_SOURCING_API_URL;

const BLANK = "—";

// Last-resort label when the form isn't available: "current_ctc" → "Current ctc".
const humanize = (key) => String(key).replace(/_/g, " ").replace(/^./, ch => ch.toUpperCase());

// A file answer is detected from the value itself, not just the form's question
// type — the form may not have loaded, and the answer is always a stored URL.
const isFileAnswer = (key, type, value) =>
    type === "file" ||
    key === "resume_upload" ||
    (typeof value === "string" && /^https?:\/\//i.test(value));

// Render an answer the way a person would write it, not JSON.
const fmtAnswer = (v) => {
    if (v === null || v === undefined || v === "") return BLANK;
    if (typeof v === "boolean") return v ? "Yes" : "No";
    if (Array.isArray(v)) return v.length ? v.join(", ") : BLANK;
    return String(v);
};

// The S3 key is a fixed resume{ext}, so this yields "resume.pdf" / "resume.docx" —
// the candidate's original filename isn't recoverable from the URL.
const fileNameFrom = (url) => {
    try {
        return decodeURIComponent(String(url).split("?")[0].split("/").pop()) || "resume";
    } catch {
        return "resume";
    }
};

// Uploads are stored with ContentDisposition: attachment, so hitting the URL
// downloads in place rather than navigating or opening a tab. That header also
// restores the candidate's original filename, which the S3 key itself drops.
const downloadResume = (url) => {
    const a = document.createElement("a");
    a.href = url;
    a.download = fileNameFrom(url);
    document.body.appendChild(a);
    a.click();
    a.remove();
};

// One question row: green when it cleared its rule, red when it didn't, neutral
// when the question carries no rule (nothing to pass or fail).
const AnswerRow = ({ item }) => {
    const tone =
        item.state === "pass" ? { bar: "bg-green-500", chip: "bg-green-50 text-green-700 border-green-200", Icon: CheckCircle2 }
            : item.state === "fail" ? { bar: "bg-red-500", chip: "bg-red-50 text-red-600 border-red-200", Icon: XCircle }
                : { bar: "bg-gray-200", chip: "", Icon: null };
    return (
        <div className="flex gap-3">
            <span className={`w-1 rounded-full shrink-0 ${tone.bar}`} />
            <div className="min-w-0 flex-1 py-1">
                <div className="flex items-start justify-between gap-3">
                    <p className="text-sm text-gray-700 dark:text-gray-300 min-w-0">{item.label}</p>
                    {tone.Icon && (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold border shrink-0 ${tone.chip}`}>
                            <tone.Icon className="w-3 h-3" />
                            {item.state === "pass" ? "Met" : "Not met"}
                        </span>
                    )}
                </div>
                <p className={`text-sm mt-0.5 ${item.answered ? "text-gray-900 font-medium" : "text-gray-300 italic"}`}>
                    {item.answered ? fmtAnswer(item.answer) : "Not answered"}
                </p>
                {item.state !== "neutral" && (
                    <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
                        Qualifies if: <span className="text-gray-500 dark:text-gray-400">{fmtAnswer(item.ideal)}</span>
                        {item.reason ? <span> · {item.reason}</span> : null}
                    </p>
                )}
            </div>
        </div>
    );
};

// Full question-and-answer view for one candidate.
// Question labels come from the form; a "not interested" candidate never saw the
// rest of the form, so only what they actually answered is listed for them.
//
// Props are normalized (not a raw row) so both the lab page and the project Contacted
// view can feed it: candidate, responses, scoreBreakdown, isQualified, intent,
// submittedAt, formId, formQuestions (optional — self-fetched from formId when absent).
const ScreeningBreakdownModal = ({
    candidate,
    responses,
    scoreBreakdown,
    isQualified,
    intent,
    submittedAt,
    formId,
    formQuestions,
    onClose,
}) => {
    // Falls back to fetching the form when questions weren't passed in — otherwise
    // labels degrade to raw keys and unanswered questions can't be listed.
    const [fetchedQuestions, setFetchedQuestions] = useState(null);

    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    useEffect(() => {
        if (formQuestions?.length || !formId) return;
        axios
            .get(`${SOURCING_URL}/engagement/forms/${formId}`)
            .then(({ data }) => setFetchedQuestions(data.questions || []))
            .catch(() => setFetchedQuestions([]));
    }, [formQuestions, formId]);

    const c = candidate || {};
    const bd = Array.isArray(scoreBreakdown) ? scoreBreakdown : [];
    const notInterested = (responses || {}).interested === false || intent === "not_interested";

    // The resume is an attachment, not a Q&A item — it's lifted out of the answer
    // list into a header action, so its question row is dropped below.
    const resumeUrl = useMemo(() => {
        const ans = responses || {};
        const key = Object.keys(ans).find(k => ans[k] && isFileAnswer(k, undefined, ans[k]));
        return key ? ans[key] : null;
    }, [responses]);

    const items = useMemo(() => {
        const ans = responses || {};
        const arr = Array.isArray(scoreBreakdown) ? scoreBreakdown : [];
        const gates = Object.fromEntries(arr.map(b => [b.key, b]));
        const declined = ans.interested === false || intent === "not_interested";
        const qs = (formQuestions?.length ? formQuestions : fetchedQuestions) || [];
        const answeredKeys = Object.keys(ans);
        const qFor = (key) => qs.find(q => q.key === key);
        const labelFor = (key) => qFor(key)?.label || gates[key]?.label || humanize(key);

        // Not interested → only what they answered, since they never saw the rest.
        // Interested → the whole form, unanswered questions included.
        // "interested" is omitted (the Intent column shows it) and the resume is
        // omitted (it's the header's Download action).
        const keys = (declined
            ? answeredKeys
            : [...qs.map(q => q.key), ...answeredKeys.filter(k => !qs.some(q => q.key === k))]
        ).filter(k => k !== "interested" && !isFileAnswer(k, qFor(k)?.type, ans[k]));

        return keys.map(key => {
            const gate = gates[key];
            const answered = key in ans && ans[key] !== null && ans[key] !== "";
            return {
                key,
                label: labelFor(key),
                answer: ans[key],
                answered,
                state: gate ? (gate.matched ? "pass" : "fail") : "neutral",
                ideal: gate?.ideal_answer,
                reason: gate?.reason,
            };
        });
    }, [responses, intent, scoreBreakdown, formQuestions, fetchedQuestions]);

    // Until the question source resolves, `items` falls back to raw answer-key order
    // and humanized labels — so hold the list rather than paint an order/labels that
    // reshuffle once the form lands. False when no form is coming (nothing to wait for).
    const awaitingQuestions = !formQuestions?.length && !!formId && fetchedQuestions === null;

    const failed = items.filter(i => i.state === "fail").length;
    const banner = notInterested
        ? { cls: "bg-red-50 border-red-200 text-red-700", Icon: XCircle, text: "Declined the opportunity — remaining answers were not evaluated." }
        : isQualified === false
            ? { cls: "bg-red-50 border-red-200 text-red-700", Icon: XCircle, text: `Not qualified — ${failed} requirement${failed === 1 ? "" : "s"} not met.` }
            : bd.length === 0
                ? { cls: "bg-gray-50 border-gray-200 text-gray-600", Icon: Info, text: "No qualifying rules on this form — auto-qualified." }
                : { cls: "bg-green-50 border-green-200 text-green-700", Icon: CheckCircle2, text: "Qualified — all requirements met." };

    return (
        <>
            <div className="fixed inset-0 bg-black/50 z-[80]" onClick={onClose} />
            <div className="fixed inset-0 z-[90] flex items-center justify-center p-4 pointer-events-none">
                <div
                    className="bg-white dark:bg-[#1B1524] rounded-2xl shadow-2xl w-full max-w-2xl max-h-[88vh] flex flex-col pointer-events-auto"
                    onClick={e => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-gray-100 dark:border-gray-700/50 shrink-0">
                        <div className="flex items-center gap-3 min-w-0">
                            <CandidateAvatar candidate={c} size={40} />
                            <div className="min-w-0">
                                <div className="flex items-center gap-1.5">
                                    <h2 className="text-base font-semibold text-gray-900 dark:text-white truncate">{c.full_name || "Unknown"}</h2>
                                    <LinkedInLink url={c.linkedin_url} />
                                </div>
                                <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
                                    {c.email || "no email"}
                                    {submittedAt ? ` · responded ${new Date(submittedAt).toLocaleDateString()}` : ""}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            {resumeUrl && (
                                <button
                                    type="button"
                                    onClick={() => downloadResume(resumeUrl)}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white hover:opacity-90 transition-opacity"
                                    style={{ background: "linear-gradient(to right, #ec4899, #3b82f6)" }}
                                >
                                    <Download className="w-3.5 h-3.5" /> Download Resume
                                </button>
                            )}
                            <button onClick={onClose} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200" title="Close">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                    {/* Verdict */}
                    <div className="px-6 pt-4 shrink-0">
                        <div className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm ${banner.cls}`}>
                            <banner.Icon className="w-4 h-4 shrink-0" />
                            {banner.text}
                        </div>
                    </div>

                    {/* Answers */}
                    <div className="flex-1 overflow-y-auto px-6 py-4">
                        {awaitingQuestions ? (
                            // Hold for the form's question order/labels — paint once, don't reshuffle.
                            <div className="flex flex-col gap-3">
                                {[...Array(4)].map((_, i) => (
                                    <div key={i} className="flex gap-3 animate-pulse">
                                        <span className="w-1 rounded-full shrink-0 bg-gray-100 dark:bg-white/10" />
                                        <div className="flex-1 flex flex-col gap-2 py-1">
                                            <div className="h-3 bg-gray-100 dark:bg-white/10 rounded w-1/3" />
                                            <div className="h-3 bg-gray-50 dark:bg-white/[0.03] rounded w-1/2" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : items.length === 0 ? (
                            <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-8">
                                {notInterested
                                    ? "No further answers — the candidate declined at the first question."
                                    : "No answers submitted."}
                            </p>
                        ) : (
                            <div className="flex flex-col gap-3">
                                {items.map(item => <AnswerRow key={item.key} item={item} />)}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default ScreeningBreakdownModal;
